/** Read-only checks for specialist facts against the books.
 * Inventory accounts are asset accounts the chart marks as inventory, plus
 * the stock accounts in the seeded charts (their subtype is `current`).
 * A payroll row can be posted only when gross pay equals tax withheld plus
 * net pay, and any tax lines add up to the tax withheld. The chart accounts
 * on those lines come from the review response, not from this check.
 */

interface InventoryAccountRef {
  id: string;
  code: string;
  name: string;
  account_type: string;
  sub_type?: string;
}

interface TrialBalanceAmount {
  account_id?: string;
  debit_cents?: number;
  credit_cents?: number;
  amount_cents?: number;
}

export interface InventoryAccountBalance {
  id: string;
  code: string;
  name: string;
  balance_cents: number;
}

export interface InventoryBooksReview {
  accounts: InventoryAccountBalance[];
  book_balance_cents: number | null;
  average_difference_cents: number | null;
  fifo_difference_cents: number | null;
  unvalued_count: number;
  problem: "none" | "no_account" | "unknown" | "overflow";
}

interface PayrollAmountInput {
  gross_cents: number;
  tax_cents: number;
  net_cents: number;
  employer_tax_cents: number;
  tax_lines: { amount_cents: number }[];
}

interface PayrollAmountReview {
  status: "post" | "skip" | "refuse";
  reason: string | null;
}

const STOCK_NAMES = new Set([
  "raw materials",
  "finished goods",
  "goods for resale",
]);

export function isInventoryAssetAccount(account: {
  account_type: string;
  sub_type?: string;
  name: string;
}): boolean {
  if (account.account_type !== "asset") return false;
  if (account.sub_type === "inventory") return true;
  const name = account.name.trim().toLowerCase();
  return name.includes("inventory") || STOCK_NAMES.has(name);
}

function subtract(left: number, right: number): number | null {
  if (!Number.isSafeInteger(left) || !Number.isSafeInteger(right)) return null;
  const difference = left - right;
  return Number.isSafeInteger(difference) ? difference : null;
}

function add(left: number, right: number): number | null {
  if (!Number.isSafeInteger(left) || !Number.isSafeInteger(right)) return null;
  const sum = left + right;
  return Number.isSafeInteger(sum) ? sum : null;
}

function rowBalance(row: TrialBalanceAmount): number | null | "missing" {
  const hasDebit = row.debit_cents !== undefined;
  const hasCredit = row.credit_cents !== undefined;
  if (hasDebit || hasCredit) {
    return subtract(row.debit_cents ?? 0, row.credit_cents ?? 0);
  }
  if (row.amount_cents === undefined) return "missing";
  return Number.isSafeInteger(row.amount_cents) ? row.amount_cents : null;
}

export function reviewInventoryBooks(input: {
  accounts: InventoryAccountRef[];
  rows: TrialBalanceAmount[];
  total_average_value_cents: number;
  total_fifo_value_cents: number;
  unvalued_count: number;
}): InventoryBooksReview {
  const selected = input.accounts.filter(isInventoryAssetAccount);
  if (selected.length === 0) {
    return {
      accounts: [],
      book_balance_cents: null,
      average_difference_cents: null,
      fifo_difference_cents: null,
      unvalued_count: input.unvalued_count,
      problem: "no_account",
    };
  }

  const byAccount = new Map<string, TrialBalanceAmount[]>();
  for (const row of input.rows) {
    if (!row.account_id) continue;
    const rows = byAccount.get(row.account_id) ?? [];
    rows.push(row);
    byAccount.set(row.account_id, rows);
  }

  const balances: InventoryAccountBalance[] = [];
  let book = 0;
  for (const account of selected) {
    const rows = byAccount.get(account.id) ?? [];
    if (rows.length > 1) {
      return emptyProblem(input.unvalued_count, "unknown");
    }
    const balance = rows.length === 0 ? 0 : rowBalance(rows[0]);
    if (balance === "missing" || balance === null) {
      return emptyProblem(input.unvalued_count, balance === null ? "overflow" : "unknown");
    }
    const next = add(book, balance);
    if (next === null) return emptyProblem(input.unvalued_count, "overflow");
    book = next;
    balances.push({
      id: account.id,
      code: account.code,
      name: account.name,
      balance_cents: balance,
    });
  }

  if (
    !Number.isSafeInteger(input.total_average_value_cents) ||
    !Number.isSafeInteger(input.total_fifo_value_cents)
  ) {
    return emptyProblem(input.unvalued_count, "overflow");
  }
  const averageDifference = subtract(book, input.total_average_value_cents);
  const fifoDifference = subtract(book, input.total_fifo_value_cents);
  if (averageDifference === null || fifoDifference === null) {
    return emptyProblem(input.unvalued_count, "overflow");
  }
  return {
    accounts: balances,
    book_balance_cents: book,
    average_difference_cents: averageDifference,
    fifo_difference_cents: fifoDifference,
    unvalued_count: input.unvalued_count,
    problem: "none",
  };
}

function emptyProblem(
  unvaluedCount: number,
  problem: "unknown" | "overflow",
): InventoryBooksReview {
  return {
    accounts: [],
    book_balance_cents: null,
    average_difference_cents: null,
    fifo_difference_cents: null,
    unvalued_count: unvaluedCount,
    problem,
  };
}

export function reviewPayrollAmounts(entry: PayrollAmountInput): PayrollAmountReview {
  if (
    !Number.isSafeInteger(entry.gross_cents) ||
    !Number.isSafeInteger(entry.tax_cents) ||
    !Number.isSafeInteger(entry.net_cents) ||
    !Number.isSafeInteger(entry.employer_tax_cents)
  ) {
    return { status: "refuse", reason: "These amounts are too large to add together." };
  }
  if (entry.gross_cents <= 0) {
    return {
      status: "skip",
      reason: "Gross pay is not positive, so this row stays off the ledger.",
    };
  }
  const withheldAndNet = add(entry.tax_cents, entry.net_cents);
  if (withheldAndNet === null) {
    return { status: "refuse", reason: "These amounts are too large to add together." };
  }
  if (withheldAndNet !== entry.gross_cents) {
    return {
      status: "refuse",
      reason: "Gross pay must equal tax withheld plus net pay before this journal can be posted.",
    };
  }
  if (entry.tax_lines.length > 0) {
    let lineTotal = 0;
    for (const line of entry.tax_lines) {
      if (!Number.isSafeInteger(line.amount_cents)) {
        return { status: "refuse", reason: "These amounts are too large to add together." };
      }
      const next = add(lineTotal, Math.max(0, line.amount_cents));
      if (next === null) {
        return { status: "refuse", reason: "These amounts are too large to add together." };
      }
      lineTotal = next;
    }
    if (lineTotal !== Math.max(0, entry.tax_cents)) {
      return {
        status: "refuse",
        reason: "The tax lines do not add up to the tax withheld.",
      };
    }
  }
  return { status: "post", reason: null };
}
